import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Box,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOffOutlined } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { salesLogin } from "../../../store/actions/salesActions";

const SalesLogin = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const mobileSize = useMediaQuery(theme.breakpoints.down("sm"));
  const tabletSize = useMediaQuery(theme.breakpoints.down("md"));
  const miniSize = useMediaQuery("(max-width:330px)");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (error !== "") {
      const timer = setTimeout(() => {
        setError("");
      }, 6000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [error]);

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Email address must be a valid email")
        .required("Email is required"),
      password: Yup.string().required("Password is required"),
    }),
    onSubmit: async (values, helpers) => {
      try {
        setLoading(true);
        setError("");

        const result = await dispatch(
          salesLogin(values.email, values.password)
        );

        if (result?.success) {
          // Login successful - Redux state is already updated
          // Navigate to dashboard
          navigate("/sales/dashboard");
        } else {
          // Login failed
          setError(result?.message || "Login failed");
          setLoading(false);
        }
      } catch (err) {
        setLoading(false);
        setError(err.response?.data?.message || "An error occurred");
      }
    },
  });

  return (
    <>
      <Box
        sx={
          mobileSize
            ? {
                justifyContent: "center",
                overflow: "auto",
                minHeight: "100vh",
                overflowY: "auto",
                display: "flex",
                mx: 0,
                flexDirection: "column",
                backgroundColor: "#fff",
              }
            : {
                backgroundImage: "url('/img/website_img/homeImg.jpeg')",
                backgroundRepeat: "repeat",
                minHeight: "100vh",
                overflow: "hidden",
                alignItems: "center",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                px: tabletSize ? 2 : 0,
              }
        }
      >
        <Box
          sx={
            mobileSize
              ? {
                  backgroundColor: "#fff",
                  width: "100%",
                  flexGrow: 1,
                  py: "1rem",
                }
              : {
                  backgroundColor: "#fff",
                  mt: 2,
                  maxWidth: 700,
                  borderRadius: "25px",
                  borderBottomLeftRadius: "0px",
                  borderBottomRightRadius: "0px",
                  px: tabletSize ? 2 : 3,
                  py: "10px",
                  width: "100%",
                  pt: "0.5rem",
                }
          }
        >
          <Stack
            spacing={1}
            sx={{ mb: mobileSize ? 2 : 3, px: mobileSize ? 2 : 0 }}
          >
            <Box display="flex" justifyContent="center">
              <img
                style={{
                  width: miniSize ? "8rem" : "9rem",
                  marginLeft: "auto",
                  marginRight: "auto",
                  marginTop: mobileSize ? "0px" : "-9px",
                }}
                alt="Permit Rockstar Logo"
                src="/img/logos/logo_main.png"
              />
            </Box>

            <Typography
              sx={{
                textAlign: "center",
                fontSize: mobileSize ? "1.1rem" : "1.25rem",
                px: mobileSize ? 1 : 0,
              }}
              color="#004976"
              variant="h6"
            >
              Welcome to Permit Rockstar Sales Team
            </Typography>
            <Typography
              sx={{
                textAlign: "center",
                lineHeight: mobileSize ? "1.5" : "3px",
                pb: mobileSize ? "0.5rem" : "1rem",
                px: mobileSize ? 1 : 0,
              }}
              color="text.secondary"
              variant="body2"
            >
              Please sign in to access your dashboard
            </Typography>
            {mobileSize && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  py: 2,
                }}
              >
                <img
                  src="/img/logos/logo_main.png"
                  alt="Sales Team Avatar"
                  style={{
                    width: "60%",
                    height: "auto",
                    objectFit: "contain",
                  }}
                />
              </Box>
            )}
          </Stack>
        </Box>
        <Box
          sx={
            !mobileSize
              ? {
                  backgroundColor: "#fff",
                  mb: 2,
                  maxWidth: 700,
                  borderRadius: "25px",
                  borderTopLeftRadius: "0px",
                  borderTopRightRadius: "0px",
                  px: tabletSize ? 2 : 3,
                  py: "10px",
                  width: "100%",
                }
              : {
                  mb: 3,
                  backgroundColor: "#fff",
                  width: "100%",
                  py: mobileSize ? 2 : 0,
                }
          }
        >
          <form
            noValidate
            autoComplete="off"
            onSubmit={formik.handleSubmit}
            style={
              mobileSize
                ? { paddingLeft: "1rem", paddingRight: "1rem" }
                : tabletSize
                  ? { paddingLeft: "0.5rem", paddingRight: "0.5rem" }
                  : {}
            }
          >
            <Stack
              spacing={
                !!(formik.touched.email && formik.errors.email) &&
                !!(formik.touched.password && formik.errors.password)
                  ? 1
                  : 2
              }
              sx={{ px: mobileSize ? 0 : 0 }}
            >
              <TextField
                error={!!(formik.touched.email && formik.errors.email)}
                fullWidth
                helperText={formik.touched.email && formik.errors.email}
                label="Email Address"
                name="email"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                type="email"
                value={formik.values.email}
              />
              <TextField
                error={!!(formik.touched.password && formik.errors.password)}
                type={showPassword ? "text" : "password"}
                fullWidth
                helperText={formik.touched.password && formik.errors.password}
                label="Password"
                name="password"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.password}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          onMouseDown={(e) => e.preventDefault()}
                          onMouseUp={(e) => e.preventDefault()}
                          edge="end"
                        >
                          {showPassword ? (
                            <Visibility />
                          ) : (
                            <VisibilityOffOutlined />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Stack>

            {formik.errors.submit && (
              <Typography color="error" sx={{ mt: 3 }} variant="body2">
                {formik.errors.submit}
              </Typography>
            )}
            <Typography
              variant="body1"
              sx={{
                minHeight: "1.5rem",
                fontSize: mobileSize ? "0.875rem" : "1rem",
                px: mobileSize ? 1 : 0,
              }}
              textAlign="center"
              my={1}
              color="error"
              fontWeight="bold"
            >
              {error}
            </Typography>

            <LoadingButton
              loading={loading}
              fullWidth
              size="large"
              sx={{
                mt: mobileSize ? 1 : 0,
                mb: mobileSize ? 2 : 0,
                px: mobileSize ? 0 : 0,
              }}
              type="submit"
              variant="contained"
            >
              Sign In
            </LoadingButton>
          </form>
        </Box>
      </Box>
    </>
  );
};

export default SalesLogin;
